import * as Sentry from '@sentry/react';
import { SOCKET_CLOSE_CODES } from 'socket/socket.constants';
import { isConnectionLimitError } from 'socket/socket.utils';

interface Props {
  cookies: any;
  page: any;
  address: any;
}

interface requestFormatRPC {
  method: string;
  params: { [key: string]: any };
  id: string;
}

interface responseFormatRPC {
  result: any;
  error: any;
  id: string;
}

//TODO: какой-то сокетный функционал либо на классах либо функцией (разобраться с MST!)
// Пока что экспортируется функция.

export const socketService = (data: Props) => {
  const wsProtocol = window.location.protocol === 'http:' ? 'ws:' : 'wss:';
  const webSocket = new WebSocket(
    `${wsProtocol}//${window.location.host}/api/v4/ws/updates/`
  );
};

export class webSocketService {
  webSocket: any = null;
  subscribers: any = {};
  prefix: string;
  api: string;

  // Поля для автоматического переподключения
  private reconnectAttempts: number = 0;
  private readonly maxReconnectAttempts = 10;
  private readonly baseReconnectDelay = 1000; // 1 секунда
  private readonly maxReconnectDelay = 30000; // 30 секунд
  private reconnectTimeout: number | null = null;
  private manualClose = false;
  private pendingCloseOnOpen = false;
  private connectionLimitReached = false;

  constructor(prefix: string, api?: string) {
    this.prefix = prefix;
    this.api = api ? api + '/' : '';

    // При активации вкладки пытаемся переподключиться если был лимит
    document.addEventListener('visibilitychange', () => {
      if (
        !document.hidden &&
        this.connectionLimitReached &&
        !this.webSocket &&
        !this.manualClose
      ) {
        console.log(
          `[webSocketService:${this.api}] вкладка активирована, пробуем переподключиться после лимита`
        );
        this.connectionLimitReached = false;
        this.connect();
      }
    });
  }

  /*
    Как использовалось в старой системе:

    1. webSocketService.connect() инициирует соединение сокета, делается 1 раз на все приложение.
    2. webSocketService.subscribe(<Название события>, callback) подписывается на события, приходящие по ws-соединению.

    По необходимости можно подписаться на внутренние ws-события, например, 'onopen'
 */

  private addListeners(self = this) {
    this.webSocket.onmessage = function (event: { [key: string]: any }) {
      const data = JSON.parse(event.data);

      // Проверка на ошибку лимита соединений
      if (data.status === 'failure' && isConnectionLimitError(data.message)) {
        self.connectionLimitReached = true;
        const message = `[webSocketService:${self.api}] Лимит WebSocket соединений исчерпан: ${data.message}`;
        console.error(message);
        Sentry.captureMessage(message, 'warning');
        // Закрываем соединение чтобы onclose не начал переподключение
        if (self.webSocket) {
          self.webSocket.close();
        }
        return;
      }

      self.notifySubscribers(data.event, data.data, data.meta);
    };

    this.webSocket.onopen = function () {
      // Если close() был вызван до завершения handshake (CONNECTING),
      // закрываем соединение сразу после успешного открытия — без ошибки
      // "WebSocket is closed before the connection is established".
      if (self.pendingCloseOnOpen) {
        self.pendingCloseOnOpen = false;
        if (self.webSocket?.readyState === WebSocket.OPEN) {
          self.webSocket.close();
        }
        return;
      }

      self.reconnectAttempts = 0; // Сброс счётчика при успешном подключении
      self.notifySubscribers(
        'onopen',
        {
          data: 'OPENED',
        },
        {}
      );
    };

    this.webSocket.onclose = function (event: CloseEvent) {
      console.log(
        `[webSocketService:${self.api}] соединение закрыто, код: ${event.code}, причина: ${event.reason || '—'}`
      );
      self.pendingCloseOnOpen = false;
      self.webSocket = null;

      if (self.manualClose) {
        self.manualClose = false;
        return;
      }

      // Лимит соединений (флаг установлен в onmessage) — не пытаемся переподключаться
      if (self.connectionLimitReached) {
        console.log(
          `[webSocketService:${self.api}] лимит соединений достигнут, переподключение отменено`
        );
        return;
      }

      // Лимит соединений (по коду/reason) — не пытаемся переподключаться
      if (
        event.code === SOCKET_CLOSE_CODES.CONNECTION_LIMIT ||
        isConnectionLimitError(event.reason)
      ) {
        self.connectionLimitReached = true;
        const message = `[webSocketService:${self.api}] Лимит WebSocket соединений исчерпан. Код: ${event.code}, причина: ${event.reason}`;
        console.error(message);
        Sentry.captureMessage(message, 'warning');
        return;
      }

      self.scheduleReconnect();
    };

    this.webSocket.onerror = function (error: Event) {
      console.error(`[webSocketService:${self.api}] ошибка:`, error);
    };
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log(
        `[webSocketService:${this.api}] достигнут лимит переподключений (${this.maxReconnectAttempts})`
      );
      return;
    }

    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    );

    this.reconnectAttempts++;
    console.log(
      `[webSocketService:${this.api}] переподключение #${this.reconnectAttempts} через ${delay}мс`
    );

    this.reconnectTimeout = window.setTimeout(() => {
      this.connect();
    }, delay);
  }

  private notifySubscribers(event: string, data: any, meta: any) {
    const list = this.subscribers[event];

    if (list) {
      list.forEach((el: any) => {
        el.callback(event, data, meta);
      });
    }
  }

  connect() {
    if (!this.webSocket) {
      const wsProtocol = window.location.protocol === 'http:' ? 'ws:' : 'wss:';
      let url = `${wsProtocol}//${window.location.host}/${this.prefix}/ws`;

      if (this.api) {
        url += `/${this.api}`;
      }

      this.webSocket = new WebSocket(url);
      this.addListeners();
    }
  }

  subscribe(events: any[], callback: any) {
    if (events && events.length && callback) {
      events.forEach((event: any) => {
        if (!this.subscribers[event]) {
          this.subscribers[event] = [];
        }
        this.subscribers[event].push({
          event: event,
          callback: callback,
        });
      });
    }
  }

  unsubscribe(events: any[], callback: any) {
    if (events?.length) {
      events.forEach((event: any) => {
        if (this.subscribers[event]) {
          this.subscribers[event] = this.subscribers[event].filter(
            (subscriber: any) => subscriber.callback !== callback
          );
        }
      });
    }
  }

  send(event: any, data: any) {
    if (event && data) {
      this.webSocket.send(JSON.stringify({ event, data }));
    }
  }

  newSend(data: Record<string, unknown>) {
    if (this.webSocket?.readyState === WebSocket.OPEN) {
      this.webSocket.send(JSON.stringify(data));
    }
  }

  close() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.webSocket) {
      console.warn(
        `[webSocketService:${this.api}] manual close() вызван. readyState=${this.webSocket.readyState}`,
        new Error().stack
      );
      this.manualClose = true;
      if (this.webSocket.readyState === WebSocket.CONNECTING) {
        // Не закрываем сокет до handshake: отложим закрытие до onopen.
        this.pendingCloseOnOpen = true;
        return;
      }
      this.pendingCloseOnOpen = false;
      this.webSocket.close();
      this.webSocket = null;
    }
  }
}
